import { getApiError } from '../lib/apiError';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  DollarSign, CreditCard, QrCode, Banknote, TrendingUp, TrendingDown,
  Calculator, Printer, Lock, Unlock, Calendar, Clock, User, FileText,
  CheckCircle, XCircle, AlertTriangle, Loader2, History, ArrowUpCircle,
  ArrowDownCircle, Receipt, Wallet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

const paymentMethodLabels = {
  dinheiro: 'Dinheiro',
  cartao_credito: 'Cartão Crédito',
  cartao_debito: 'Cartão Débito',
  pix: 'PIX',
  pix_cnpj: 'PIX - CNPJ',
  crediario: 'Crediário',
  crediario_bemol: 'Crediário Bemol',
  avancard: 'Avancard',
  vale_mercadoria: 'Vale Mercadoria',
  multiplo: 'Múltiplo'
};

const CashClosing = () => {
  const [loading, setLoading] = useState(true);
  const [currentData, setCurrentData] = useState(null);
  const [history, setHistory] = useState([]);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedClosing, setSelectedClosing] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [openForm, setOpenForm] = useState({ initial_balance: '', notes: '' });
  const [closeForm, setCloseForm] = useState({ actual_balance: '', notes: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/pdv/cash-closing/current`),
        axios.get(`${API_URL}/api/pdv/cash-closing/history?limit=10`)
      ]);
      setCurrentData(currentRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCash = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/pdv/cash-closing/open`, null, {
        params: {
          initial_balance: parseFloat(openForm.initial_balance) || 0,
          notes: openForm.notes || null
        }
      });
      toast.success('Caixa aberto com sucesso!');
      setShowOpenDialog(false);
      setOpenForm({ initial_balance: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao abrir caixa'));
    } finally {
      setSaving(false);
    }
  };

  const handleCloseCash = async () => {
    if (!closeForm.actual_balance) {
      toast.error('Informe o valor em caixa');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/pdv/cash-closing/close`, null, {
        params: {
          actual_balance: parseFloat(closeForm.actual_balance),
          notes: closeForm.notes || null
        }
      });
      toast.success(response.data.message);
      setShowCloseDialog(false);
      setCloseForm({ actual_balance: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao fechar caixa'));
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetail = async (closingId) => {
    try {
      const response = await axios.get(`${API_URL}/api/pdv/cash-closing/${closingId}`);
      setSelectedClosing(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  const summary = currentData?.summary || {};
  const isOpen = currentData?.is_open;
  const cashFromPreviousDay = currentData?.cash_from_previous_day;
  const cashDate = currentData?.cash_date;

  return (
    <div className="space-y-6" data-testid="cash-closing-page">
      {/* Aviso de caixa do dia anterior */}
      {isOpen && cashFromPreviousDay && (
        <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 flex items-start gap-3" data-testid="previous-day-warning">
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={22} />
          <div>
            <p className="font-semibold text-amber-800">
              Caixa do dia {cashDate && new Date(cashDate + 'T12:00:00').toLocaleDateString('pt-BR')} ainda aberto!
            </p>
            <p className="text-sm text-amber-700 mt-1">
              A operadora esqueceu de fechar o caixa. Feche o caixa anterior para poder abrir o caixa de hoje.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fechamento de Caixa</h1>
          <p className="text-slate-500 mt-1">
            {isOpen && cashFromPreviousDay ? (
              <>Caixa aberto em {cashDate && new Date(cashDate + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</>
            ) : (
              currentData?.date && new Date(currentData.date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowHistoryDialog(true)} data-testid="btn-history">
            <History size={18} className="mr-2" />
            Histórico
          </Button>
          {!isOpen ? (
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowOpenDialog(true)} data-testid="btn-open-cash">
              <Unlock size={18} className="mr-2" />
              Abrir Caixa
            </Button>
          ) : (
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => setShowCloseDialog(true)} data-testid="btn-close-cash">
              <Lock size={18} className="mr-2" />
              Fechar Caixa
            </Button>
          )}
        </div>
      </div>

      {/* Status do Caixa */}
      <Card className={`border-2 ${isOpen ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-slate-50'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isOpen ? (
                <div className="p-3 rounded-full bg-emerald-100">
                  <Unlock className="text-emerald-600" size={24} />
                </div>
              ) : (
                <div className="p-3 rounded-full bg-slate-200">
                  <Lock className="text-slate-500" size={24} />
                </div>
              )}
              <div>
                <p className="font-medium text-lg">
                  {isOpen ? 'Caixa Aberto' : 'Caixa Fechado'}
                </p>
                {currentData?.closing && (
                  <p className="text-sm text-slate-500">
                    {currentData.closing.closing_number} • 
                    Aberto por {currentData.closing.opened_by_name} às {new Date(currentData.closing.opened_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {isOpen && (
                <Badge className="text-white text-lg px-4 py-2 bg-emerald-600">
                  {formatCurrency(summary.expected_balance)}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <DollarSign className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Vendas</p>
                <p className="text-xl font-bold text-blue-600">{formatCurrency(summary.total_sales)}</p>
                <p className="text-xs text-slate-400">{summary.sales_count || 0} vendas</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <Banknote className="text-emerald-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Dinheiro</p>
                <p className="text-xl font-bold text-emerald-600">{formatCurrency(summary.cash_sales)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <CreditCard className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Cartão Crédito</p>
                <p className="text-xl font-bold text-purple-600">{formatCurrency(summary.card_credit || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <CreditCard className="text-orange-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Cartão Débito</p>
                <p className="text-xl font-bold text-orange-600">{formatCurrency(summary.card_debit || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-100">
                <QrCode className="text-cyan-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-500">PIX</p>
                <p className="text-xl font-bold text-cyan-600">{formatCurrency(summary.pix_sales)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cards Adicionais - Novas Formas de Pagamento */}
      {(summary.pix_cnpj_sales > 0 || summary.crediario_bemol > 0 || summary.vale_mercadoria > 0 || summary.avancard > 0 || summary.other_sales > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {summary.pix_cnpj_sales > 0 && (
            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-100">
                    <QrCode className="text-cyan-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">PIX - CNPJ</p>
                    <p className="text-xl font-bold text-cyan-600">{formatCurrency(summary.pix_cnpj_sales)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {summary.crediario_bemol > 0 && (
            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100">
                    <CreditCard className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Crediário Bemol</p>
                    <p className="text-xl font-bold text-amber-600">{formatCurrency(summary.crediario_bemol)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {summary.vale_mercadoria > 0 && (
            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-teal-100">
                    <Receipt className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Vale Mercadoria</p>
                    <p className="text-xl font-bold text-teal-600">{formatCurrency(summary.vale_mercadoria)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {summary.avancard > 0 && (
            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-indigo-100">
                    <CreditCard className="text-indigo-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Avancard</p>
                    <p className="text-xl font-bold text-indigo-600">{formatCurrency(summary.avancard)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {summary.other_sales > 0 && (
            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-100">
                    <Wallet className="text-slate-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Outros</p>
                    <p className="text-xl font-bold text-slate-600">{formatCurrency(summary.other_sales)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Movimentações e Saldo */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Movimentações */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowUpCircle className="text-red-500" size={20} />
              <ArrowDownCircle className="text-emerald-500" size={20} />
              Movimentações do Dia
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 rounded-lg bg-emerald-50">
                <div className="flex items-center gap-2">
                  <ArrowDownCircle className="text-emerald-600" size={20} />
                  <span className="font-medium">Suprimentos</span>
                </div>
                <span className="text-lg font-bold text-emerald-600">+ {formatCurrency(summary.total_suprimento)}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 rounded-lg bg-red-50">
                <div className="flex items-center gap-2">
                  <ArrowUpCircle className="text-red-600" size={20} />
                  <span className="font-medium">Sangrias</span>
                </div>
                <span className="text-lg font-bold text-red-600">- {formatCurrency(summary.total_sangria)}</span>
              </div>

              {currentData?.movements?.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm text-slate-500 mb-2">Últimas movimentações:</p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {currentData.movements.slice(0, 5).map((m) => (
                      <div key={m.id} className="flex justify-between text-sm">
                        <span className={m.type === 'sangria' ? 'text-red-600' : 'text-emerald-600'}>
                          {m.reason}
                        </span>
                        <span className="font-medium">
                          {m.type === 'sangria' ? '-' : '+'}{formatCurrency(m.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Resumo do Caixa */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator size={20} />
              Resumo do Caixa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-600">Saldo Inicial</span>
                <span className="font-medium">{formatCurrency(summary.initial_balance)}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-600">Vendas em Dinheiro</span>
                <span className="font-medium text-emerald-600">+ {formatCurrency(summary.cash_sales)}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-600">Suprimentos</span>
                <span className="font-medium text-emerald-600">+ {formatCurrency(summary.total_suprimento)}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-600">Sangrias</span>
                <span className="font-medium text-red-600">- {formatCurrency(summary.total_sangria)}</span>
              </div>
              <div className="flex justify-between py-3 bg-blue-50 rounded-lg px-3 mt-4">
                <span className="font-bold text-lg">Saldo Esperado</span>
                <span className="font-bold text-lg text-blue-600">{formatCurrency(summary.expected_balance)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Vendas do Dia */}
      {currentData?.sales?.length > 0 && (
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Vendas do Dia ({currentData.sales.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-64 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Venda</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead>Pagamento</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Hora</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentData.sales.map((sale) => (
                    <TableRow key={sale.id}>
                      <TableCell className="font-medium">{sale.sale_number}</TableCell>
                      <TableCell>{sale.client_name}</TableCell>
                      <TableCell>{sale.seller_name || sale.cashier_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          sale.payment_method === 'dinheiro' ? 'border-emerald-500 text-emerald-600' :
                          (sale.payment_method === 'pix' || sale.payment_method === 'pix_cnpj') ? 'border-cyan-500 text-cyan-600' :
                          'border-purple-500 text-purple-600'
                        }>
                          {paymentMethodLabels[sale.payment_method] || sale.payment_method}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(sale.total)}</TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(sale.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog Abrir Caixa */}
      <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="text-emerald-600" size={24} />
              Abrir Caixa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="initial_balance">Saldo Inicial (Fundo de Troco)</Label>
              <Input
                id="initial_balance"
                type="number"
                step="0.01"
                placeholder="0,00"
                value={openForm.initial_balance}
                onChange={(e) => setOpenForm({ ...openForm, initial_balance: e.target.value })}
                className="text-xl h-12"
                data-testid="initial-balance-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="open_notes">Observações</Label>
              <Textarea
                id="open_notes"
                placeholder="Observações opcionais..."
                value={openForm.notes}
                onChange={(e) => setOpenForm({ ...openForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            <Button 
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700" 
              onClick={handleOpenCash}
              disabled={saving}
              data-testid="confirm-open-cash"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Unlock size={18} className="mr-2" />}
              Abrir Caixa
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Fechar Caixa */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="text-red-600" size={24} />
              Fechar Caixa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Resumo */}
            <div className="p-4 rounded-lg bg-slate-50 space-y-2">
              <div className="flex justify-between">
                <span>Total de Vendas:</span>
                <span className="font-medium">{formatCurrency(summary.total_sales)}</span>
              </div>
              <div className="flex justify-between">
                <span>Vendas em Dinheiro:</span>
                <span className="font-medium">{formatCurrency(summary.cash_sales)}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="font-bold">Saldo Esperado:</span>
                <span className="font-bold text-blue-600">{formatCurrency(summary.expected_balance)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="actual_balance">Valor Contado em Caixa *</Label>
              <Input
                id="actual_balance"
                type="number"
                step="0.01"
                placeholder="0,00"
                value={closeForm.actual_balance}
                onChange={(e) => setCloseForm({ ...closeForm, actual_balance: e.target.value })}
                className="text-xl h-12"
                data-testid="actual-balance-input"
              />
              {closeForm.actual_balance && (
                <div className={`p-3 rounded-lg ${
                  Math.abs(parseFloat(closeForm.actual_balance) - summary.expected_balance) < 0.01 
                    ? 'bg-emerald-50 text-emerald-700' 
                    : parseFloat(closeForm.actual_balance) > summary.expected_balance 
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-red-50 text-red-700'
                }`}>
                  {Math.abs(parseFloat(closeForm.actual_balance) - summary.expected_balance) < 0.01 ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle size={18} />
                      <span>Caixa conferido - OK!</span>
                    </div>
                  ) : parseFloat(closeForm.actual_balance) > summary.expected_balance ? (
                    <div className="flex items-center gap-2">
                      <TrendingUp size={18} />
                      <span>Sobra de {formatCurrency(parseFloat(closeForm.actual_balance) - summary.expected_balance)}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <TrendingDown size={18} />
                      <span>Falta de {formatCurrency(summary.expected_balance - parseFloat(closeForm.actual_balance))}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="close_notes">Observações</Label>
              <Textarea
                id="close_notes"
                placeholder="Observações sobre o fechamento..."
                value={closeForm.notes}
                onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })}
                rows={2}
              />
            </div>

            <Button 
              className="w-full h-12 bg-red-600 hover:bg-red-700" 
              onClick={handleCloseCash}
              disabled={saving}
              data-testid="confirm-close-cash"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Lock size={18} className="mr-2" />}
              Confirmar Fechamento
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Histórico */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History size={24} />
              Histórico de Fechamentos
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {history.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                <p>Nenhum fechamento encontrado</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Número</TableHead>
                    <TableHead>Vendas</TableHead>
                    <TableHead>Saldo Esperado</TableHead>
                    <TableHead>Saldo Real</TableHead>
                    <TableHead>Diferença</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((closing) => (
                    <TableRow 
                      key={closing.id} 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleViewDetail(closing.id)}
                    >
                      <TableCell>{new Date(closing.date + 'T12:00:00').toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell className="font-medium">{closing.closing_number}</TableCell>
                      <TableCell>{formatCurrency(closing.total_sales)}</TableCell>
                      <TableCell>{formatCurrency(closing.expected_balance)}</TableCell>
                      <TableCell>{closing.actual_balance ? formatCurrency(closing.actual_balance) : '-'}</TableCell>
                      <TableCell>
                        {closing.difference !== undefined && closing.difference !== null ? (
                          <span className={
                            Math.abs(closing.difference) < 0.01 ? 'text-emerald-600' :
                            closing.difference > 0 ? 'text-blue-600' : 'text-red-600'
                          }>
                            {closing.difference > 0 ? '+' : ''}{formatCurrency(closing.difference)}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={closing.status === 'fechado' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                          {closing.status === 'fechado' ? 'Fechado' : 'Aberto'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalhes */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <FileText size={24} />
                Relatório de Fechamento
              </DialogTitle>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer size={16} className="mr-2" />
                Imprimir
              </Button>
            </div>
          </DialogHeader>
          {selectedClosing && (
            <div className="space-y-6 print:space-y-4" id="print-report">
              {/* Cabeçalho */}
              <div className="text-center border-b pb-4">
                <h2 className="text-xl font-bold">RELATÓRIO DE FECHAMENTO DE CAIXA</h2>
                <p className="text-slate-500">
                  {selectedClosing.closing.closing_number} • {new Date(selectedClosing.closing.date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>

              {/* Informações do Fechamento */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-slate-50">
                  <h3 className="font-medium mb-2">Abertura</h3>
                  <p className="text-sm">Operador: {selectedClosing.closing.opened_by_name}</p>
                  <p className="text-sm">Horário: {new Date(selectedClosing.closing.opened_at).toLocaleString('pt-BR')}</p>
                  <p className="text-sm">Saldo Inicial: {formatCurrency(selectedClosing.closing.initial_balance)}</p>
                </div>
                {selectedClosing.closing.status === 'fechado' && (
                  <div className="p-4 rounded-lg bg-slate-50">
                    <h3 className="font-medium mb-2">Fechamento</h3>
                    <p className="text-sm">Operador: {selectedClosing.closing.closed_by_name}</p>
                    <p className="text-sm">Horário: {new Date(selectedClosing.closing.closed_at).toLocaleString('pt-BR')}</p>
                    <p className="text-sm">Saldo Contado: {formatCurrency(selectedClosing.closing.actual_balance)}</p>
                  </div>
                )}
              </div>

              {/* Resumo de Vendas */}
              <div className="p-4 rounded-lg border">
                <h3 className="font-medium mb-3">Resumo de Vendas por Forma de Pagamento</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {/* Métodos principais - sempre visíveis */}
                  <div className="p-3 rounded-lg bg-emerald-50">
                    <p className="text-sm text-slate-500">Dinheiro</p>
                    <p className="text-lg font-bold text-emerald-600">{formatCurrency(selectedClosing.closing.cash_sales)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-purple-50">
                    <p className="text-sm text-slate-500">Cartão Crédito</p>
                    <p className="text-lg font-bold text-purple-600">{formatCurrency(selectedClosing.closing.card_credit || 0)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-orange-50">
                    <p className="text-sm text-slate-500">Cartão Débito</p>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(selectedClosing.closing.card_debit || 0)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-cyan-50">
                    <p className="text-sm text-slate-500">PIX</p>
                    <p className="text-lg font-bold text-cyan-600">{formatCurrency(selectedClosing.closing.pix_sales)}</p>
                  </div>
                  
                  {/* Métodos adicionais - visíveis se tiverem valor */}
                  {(selectedClosing.closing.pix_cnpj_sales > 0) && (
                    <div className="p-3 rounded-lg bg-cyan-50">
                      <p className="text-sm text-slate-500">PIX - CNPJ</p>
                      <p className="text-lg font-bold text-cyan-600">{formatCurrency(selectedClosing.closing.pix_cnpj_sales)}</p>
                    </div>
                  )}
                  {(selectedClosing.closing.crediario_bemol > 0) && (
                    <div className="p-3 rounded-lg bg-amber-50">
                      <p className="text-sm text-slate-500">Crediário Bemol</p>
                      <p className="text-lg font-bold text-amber-600">{formatCurrency(selectedClosing.closing.crediario_bemol)}</p>
                    </div>
                  )}
                  {(selectedClosing.closing.vale_mercadoria > 0) && (
                    <div className="p-3 rounded-lg bg-teal-50">
                      <p className="text-sm text-slate-500">Vale Mercadoria</p>
                      <p className="text-lg font-bold text-teal-600">{formatCurrency(selectedClosing.closing.vale_mercadoria)}</p>
                    </div>
                  )}
                  {(selectedClosing.closing.avancard > 0) && (
                    <div className="p-3 rounded-lg bg-indigo-50">
                      <p className="text-sm text-slate-500">Avancard</p>
                      <p className="text-lg font-bold text-indigo-600">{formatCurrency(selectedClosing.closing.avancard)}</p>
                    </div>
                  )}
                  {(selectedClosing.closing.other_sales > 0) && (
                    <div className="p-3 rounded-lg bg-slate-100">
                      <p className="text-sm text-slate-500">Outros</p>
                      <p className="text-lg font-bold text-slate-600">{formatCurrency(selectedClosing.closing.other_sales)}</p>
                    </div>
                  )}
                </div>
                
                {/* Total geral */}
                <div className="mt-4 pt-4 border-t flex justify-between items-center">
                  <span className="font-bold text-lg">Total de Vendas</span>
                  <span className="font-bold text-xl text-blue-600">{formatCurrency(selectedClosing.closing.total_sales)}</span>
                </div>
              </div>

              {/* Conferência */}
              {selectedClosing.closing.status === 'fechado' && (
                <div className={`p-4 rounded-lg ${
                  Math.abs(selectedClosing.closing.difference) < 0.01 ? 'bg-emerald-50' :
                  selectedClosing.closing.difference > 0 ? 'bg-blue-50' : 'bg-red-50'
                }`}>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-slate-500">Esperado</p>
                      <p className="text-xl font-bold">{formatCurrency(selectedClosing.closing.expected_balance)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Contado</p>
                      <p className="text-xl font-bold">{formatCurrency(selectedClosing.closing.actual_balance)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Diferença</p>
                      <p className={`text-xl font-bold ${
                        Math.abs(selectedClosing.closing.difference) < 0.01 ? 'text-emerald-600' :
                        selectedClosing.closing.difference > 0 ? 'text-blue-600' : 'text-red-600'
                      }`}>
                        {selectedClosing.closing.difference > 0 ? '+' : ''}{formatCurrency(selectedClosing.closing.difference)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Observações do Fechamento */}
              {(selectedClosing.closing.open_notes || selectedClosing.closing.close_notes) && (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <h3 className="font-medium mb-2 text-amber-800">Observações</h3>
                  {selectedClosing.closing.open_notes && (
                    <div className="mb-2">
                      <p className="text-xs text-amber-600 uppercase font-medium">Abertura:</p>
                      <p className="text-sm text-amber-900">{selectedClosing.closing.open_notes}</p>
                    </div>
                  )}
                  {selectedClosing.closing.close_notes && (
                    <div>
                      <p className="text-xs text-amber-600 uppercase font-medium">Fechamento:</p>
                      <p className="text-sm text-amber-900">{selectedClosing.closing.close_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Lista de Vendas */}
              {selectedClosing.sales?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">Vendas ({selectedClosing.sales.length})</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Venda</TableHead>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Pagamento</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedClosing.sales.map((sale) => (
                          <TableRow key={sale.id}>
                            <TableCell>{sale.sale_number}</TableCell>
                            <TableCell>{sale.client_name}</TableCell>
                            <TableCell>
                              {sale.payment_method === 'multiplo' && sale.payment_methods && sale.payment_methods.length > 0 ? (
                                <div className="group relative">
                                  <span className="cursor-help border-b border-dashed border-slate-400">
                                    {paymentMethodLabels[sale.payment_method] || sale.payment_method}
                                  </span>
                                  <div className="absolute z-50 hidden group-hover:block bg-white border shadow-lg rounded-lg p-2 min-w-[150px] left-0 top-full mt-1">
                                    <p className="text-xs text-slate-500 mb-1 font-medium">Detalhamento:</p>
                                    {sale.payment_methods.map((pm, idx) => (
                                      <div key={idx} className="flex justify-between text-sm py-0.5">
                                        <span className="text-slate-600">{paymentMethodLabels[pm.method] || pm.method}</span>
                                        <span className="font-medium ml-3">{formatCurrency(pm.amount)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                paymentMethodLabels[sale.payment_method] || sale.payment_method
                              )}
                            </TableCell>
                            <TableCell className="text-right">{formatCurrency(sale.total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Sangrias e Suprimentos */}
              {selectedClosing.movements?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <ArrowUpCircle className="text-red-500" size={18} />
                    <ArrowDownCircle className="text-emerald-500" size={18} />
                    Movimentações do Dia ({selectedClosing.movements.length})
                  </h3>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Operador</TableHead>
                          <TableHead>Observação</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                          <TableHead>Horário</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedClosing.movements.map((movement) => (
                          <TableRow key={movement.id}>
                            <TableCell>
                              <Badge className={movement.type === 'sangria' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>
                                {movement.type === 'sangria' ? (
                                  <><ArrowUpCircle size={12} className="mr-1" /> Sangria</>
                                ) : (
                                  <><ArrowDownCircle size={12} className="mr-1" /> Suprimento</>
                                )}
                              </Badge>
                            </TableCell>
                            <TableCell>{movement.user_name || '-'}</TableCell>
                            <TableCell className="max-w-xs" title={movement.reason || movement.notes || movement.description}>
                              <div className="truncate">
                                {movement.reason || movement.notes || movement.description || '-'}
                              </div>
                              {movement.notes && movement.reason && (
                                <div className="text-xs text-slate-400 truncate mt-0.5">
                                  {movement.notes}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className={`text-right font-medium ${movement.type === 'sangria' ? 'text-red-600' : 'text-emerald-600'}`}>
                              {movement.type === 'sangria' ? '-' : '+'}{formatCurrency(movement.amount)}
                            </TableCell>
                            <TableCell className="text-slate-500">
                              {new Date(movement.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Resumo das movimentações */}
                  <div className="mt-3 grid grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg bg-red-50 flex justify-between items-center">
                      <span className="text-sm text-red-700">Total Sangrias:</span>
                      <span className="font-bold text-red-600">
                        -{formatCurrency(selectedClosing.movements.filter(m => m.type === 'sangria').reduce((sum, m) => sum + (m.amount || 0), 0))}
                      </span>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-50 flex justify-between items-center">
                      <span className="text-sm text-emerald-700">Total Suprimentos:</span>
                      <span className="font-bold text-emerald-600">
                        +{formatCurrency(selectedClosing.movements.filter(m => m.type === 'suprimento').reduce((sum, m) => sum + (m.amount || 0), 0))}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashClosing;
